import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core'
import { MatSnackBar } from '@angular/material/snack-bar'
import { ApiService, CrudService, EstimateModel, EstimateService, Invoice, InvoiceModel, Patient, ShopperEstimate, ShopperEstimateService } from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import { BehaviorSubject, combineLatest, filter, finalize, map, Subject, switchMap, takeUntil } from 'rxjs'

import {
  SendContactEditDialogResult,
  SendContactEditDialogService,
} from '../../send-contact-edit-dialog'

@Component({
  selector: 'mmx-estimate-send-invoice',
  templateUrl: './estimate-send-invoice.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EstimateSendInvoiceComponent implements OnChanges, OnDestroy {
  @Input()
  public estimate: EstimateModel | ShopperEstimate

  @Input()
  public shopper = false

  @Input()
  public patient: Patient

  @Output()
  public sending = new EventEmitter<boolean>()

  public eligibleInvoices: InvoiceModel[] = []
  public sending$ = new BehaviorSubject<boolean>(false)
  public loading$ = combineLatest([
    this.estimateService.estimateLoading$,
    this.sending$,
  ])
    .pipe(
      map(([loading, sending]) => loading || sending),
    )

  private onDestroy$ = new Subject<void>()

  constructor(
    private readonly apiService: ApiService,
    private readonly sendContactEditDialogService: SendContactEditDialogService,
    private readonly snackBar: MatSnackBar,
    private readonly estimateService: EstimateService,
    private readonly crudService: CrudService,
    private readonly shopperEstimateService: ShopperEstimateService,
    private readonly translateService: TranslateService,
  ) {}

  public ngOnChanges() {
    if (this.shopper) {
      this.eligibleInvoices = []
      return
    }

    const estimate = this.estimate as EstimateModel | undefined
    this.eligibleInvoices = (estimate?.invoices || []).filter((invoice) => invoice.status !== Invoice.STATUS.VOID)
  }

  public ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  canSendEmail(invoice: InvoiceModel): boolean {
    return !!this.patient?.canEmail && invoice?.status !== Invoice.STATUS.PAID
  }

  canSendSms(invoice: InvoiceModel): boolean {
    return !!this.patient?.canText && invoice?.status !== Invoice.STATUS.PAID
  }

  openSendDialog(invoice: InvoiceModel, what: 'link' | 'pdf', method?: 'email' | 'sms') {
    this.sendContactEditDialogService.open({
      method: method ?? 'email',
      phone: this.patient?.phone,
      email: this.patient?.email,
    })
      .pipe(
        takeUntil(this.onDestroy$),
        filter(Boolean),
      )
      .subscribe((result: SendContactEditDialogResult) => {
        this.onSend(invoice, what, method, result.to)
      })
  }

  onSend(invoice: InvoiceModel, what: 'link' | 'pdf', method?: 'email' | 'sms', to?: string) {
    if (!invoice?.id) {
      return
    }

    this.sending.emit(true)
    this.sending$.next(true)
    this.apiService.sendInvoiceMessage(invoice.id, { what, method, to })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.sending.emit(false)
          this.sending$.next(false)
        }),
        switchMap(() => {
          // sending message will update estimate, get updated estimate to update the UI
          return this.crudService.read(EstimateModel, this.estimate.id)
        }),
      )
      .subscribe({
        next: (estimate) => {
          this.estimateService.setEstimate(estimate)
          return this.snackBar.open('Invoice sent', undefined, { duration: 2000 })
        },
        error: () => {
          return this.snackBar.open('Failed to send invoice', undefined, { duration: 3000 })
        },
      })
  }

  canShopperSendEmail(): boolean {
    return !!(this.estimate as ShopperEstimate)?.patientEmail
  }

  canShopperSendSms(): boolean {
    return !!(this.estimate as ShopperEstimate)?.patientPhone
  }

  openShopperSendDialog(method: 'email' | 'sms', what?: 'link' | 'pdf') {
    this.sendContactEditDialogService.open({
      method,
      phone: (this.estimate as ShopperEstimate)?.patientPhone,
      email: (this.estimate as ShopperEstimate)?.patientEmail,
    })
      .pipe(
        takeUntil(this.onDestroy$),
        filter(Boolean),
      )
      .subscribe((result: SendContactEditDialogResult) => {
        this.onShopperSend(method, result.to, what)
      })
  }

  onShopperSend(method: 'email' | 'sms', to?: string, what?: 'link' | 'pdf') {
    if (!this.estimate?.id) {
      return
    }

    this.sending.emit(true)
    this.sending$.next(true)
    this.shopperEstimateService.sendMessage(this.estimate.id, { method, to, what })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.sending.emit(false)
          this.sending$.next(false)
        }),
      )
      .subscribe({
        next: () => {
          this.snackBar.open(this.translateService.instant('shopper-estimates-dashboard.messages.estimate-sent'), undefined, { duration: 2000 })
        },
        error: () => {
          this.snackBar.open(this.translateService.instant('shopper-estimates-dashboard.messages.estimate-send-error'), undefined, { duration: 3000 })
        },
      })
  }
}
